import type { PaletteOptions, TypographyVariantsOptions } from '@mui/material/styles';
import type { customization } from './Theme';
import type { TypographyStyleOptions } from '@mui/material/styles/createTypography';
import '@fontsource/montserrat/900.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/200.css';
import '@fontsource/montserrat/100.css';
/**
 * Typography used in theme
 * @param {JsonObject} theme theme customization object
 */

declare module '@mui/material/styles' {
    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        customInput?: TypographyStyleOptions;
        mainContent?: TypographyStyleOptions;
        menuCaption?: TypographyStyleOptions;
        subMenuCaption?: TypographyStyleOptions;
        commonAvatar?: TypographyStyleOptions;
        smallAvatar?: TypographyStyleOptions;
        mediumAvatar?: TypographyStyleOptions;
        largeAvatar?: TypographyStyleOptions;
    }
}

export default function themeTypography(color: PaletteOptions, customization?: customization): TypographyVariantsOptions {
    return {
        fontFamily: customization?.fontFamily || 'Montserrat',
        h6: {
            fontWeight: 500,
            color: color.grey?.[900],
            fontSize: '0.75rem'
        },
        h5: {
            fontSize: '0.875rem',
            color: color.background?.paper,
            fontWeight: 500
        },
        h4: {
            fontSize: '1rem',
            color: color.grey?.[900],
            fontWeight: 600
        },
        h3: {
            fontSize: '1.25rem',
            color: color.grey?.[900],
            fontWeight: 600
        },
        h2: {
            fontSize: '1.5rem',
            color: color.grey?.[900],
            fontWeight: 700
        },
        h1: {
            fontSize: '2.125rem',
            color: color.grey?.[900],
            fontWeight: 700,
        },
        subtitle1: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: color.grey?.[900]
        },
        subtitle2: {
            fontSize: '0.75rem',
            fontWeight: 400,
            color: color.grey?.[500]
        },
        caption: {
            fontSize: '0.75rem',
            color: color.grey?.[500],
            fontWeight: 400
        },
        body1: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: '1.334em'
        },
        body2: {
            letterSpacing: '0em',
            fontWeight: 400,
            lineHeight: '1.5em',
            color: color.grey?.[700]
        },
        button: {
            textTransform: 'capitalize',
        },
        customInput: {
            marginTop: 1,
            marginBottom: 1,            
            '& > label': {
                top: 23,
                left: 0,
                color: color.grey?.[500],
                '&[data-shrink="false"]': {
                    top: 5
                }
            },
            '& > div > input': {
                padding: '30.5px 14px 11.5px !important'
            },
            '& legend': {
                display: 'none'
            },
            '& fieldset': {
                top: 0
            }
        },
        mainContent: {
            backgroundColor: color.background?.default,
            width: '100%',
            minHeight: 'calc(100vh - 88px)',
            flexGrow: 1,
            padding: '20px',
            marginTop: '88px',
            marginRight: '20px',
            borderRadius: `${customization?.borderRadius}px`
        },
        menuCaption: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: color.grey?.[900],
            padding: '6px',
            textTransform: 'capitalize',
            marginTop: '10px'
        },
        subMenuCaption: {
            fontSize: '0.6875rem',
            fontWeight: 500,
            color: color.grey?.[500],
            textTransform: 'capitalize'
        },
        commonAvatar: {
            cursor: 'pointer',
            borderRadius: '8px'
        },
        smallAvatar: {
            width: '22px',
            height: '22px',
            fontSize: '1rem'
        },
        mediumAvatar: {
            width: '34px',
            height: '34px',
            fontSize: '1.2rem'
        },
        largeAvatar: {
            width: '44px',
            height: '44px',
            fontSize: '1.5rem'
        }
    };
}
